// Step 3: the fit result (the desktop's FitPage, top part): DFT vs tight-binding bands, the errors, the fitted
// parameters (those at a bound in orange), the output files, and the project's earlier fits.
import { Alert, Button, Grid, Group, Stack, Table, Text, Tooltip } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { notifyError, notifyOk } from "../../components/Errors";
import { Plot } from "../../components/Plot";
import { PreviousRuns } from "../../components/RunCard";
import { fitFigure } from "../../components/SkfitterPlots";
import { download, type Job, type ProjectDetail } from "../../lib/api";
import { bondParamKeys, isLimited, siteParamKeys, type ParamMap } from "../../lib/lattice";
import { useMe } from "../../lib/hooks";
import { fitToNegf } from "../../lib/negf";
import { continueInProperties } from "../../lib/properties";
import type { FitResult } from "../../lib/skfitter";
import { ParamLattice, useParamMap, type LatticePick } from "./LatticeViews";

/** The table rows a lattice pick refers to, and the bonds a table row's parameter is used on. */
export function pickKeys(pm: ParamMap | undefined, pick: LatticePick | null): Set<string> {
  if (!pm || !pick) return new Set();
  if (pick.kind === "bond") return new Set(pm.bonds[pick.index] ? bondParamKeys(pm.bonds[pick.index], pm.distance_decay, pm.non_orthogonal) : []);
  return new Set(pm.sites[pick.index] ? siteParamKeys(pm.sites[pick.index]) : []);
}

export function rowPick(pm: ParamMap | undefined, key: string): { pick: LatticePick | null; bonds: Set<number> } {
  if (!pm) return { pick: null, bonds: new Set() };
  const site = pm.sites.findIndex((s) => siteParamKeys(s).includes(key));
  if (site >= 0) return { pick: { kind: "site", index: site }, bonds: new Set() };
  const bonds = new Set<number>();
  pm.bonds.forEach((b, n) => { if (bondParamKeys(b, pm.distance_decay, pm.non_orthogonal).includes(key)) bonds.add(n); });
  return { pick: null, bonds };
}

export function fitMetrics(r: FitResult): string {
  let t = `RMSE ${r.rmse_meV.toFixed(2)} meV over ${r.info.nk} k-points × ${r.info.n_target_bands} bands`;
  if (r.report.near_fermi != null) t += `  ·  within 0.5 eV of E_F: ${r.report.near_fermi.toFixed(2)} meV`;
  if (r.report.symmetry_points != null) t += `  ·  at high-symmetry points: ${r.report.symmetry_points.toFixed(2)} meV`;
  if (r.cancelled) t += "  ·  stopped early";
  return t;
}

export function paramsTsv(r: FitResult): string {
  const lines = [["Group", "Parameter", "Value", "Start", "Bounds"].join("\t")];
  for (const p of r.rows)
    lines.push([p.group, p.key, p.value.toFixed(5), p.initial == null ? "" : String(Number(p.initial.toPrecision(4))),
      `[${Number(p.lo.toPrecision(4))}, ${Number(p.hi.toPrecision(4))}]`].join("\t"));
  return lines.join("\n");
}

export function ResultStep({ project, job, dftJobId, onOpen, onBack, onNext }: {
  project: ProjectDetail; job: Job; dftJobId?: string; onOpen: (id: string) => void; onBack: () => void; onNext: () => void;
}) {
  const r = job.status === "succeeded" ? (job.result as FitResult) : undefined;
  const { data: me } = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toNegf = useMutation({
    mutationFn: () => fitToNegf(job.id, project.name),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["me"] }); nav(`/projects/${p.id}`); },
    onError: (e) => notifyError(e),
  });
  const negf = !!(project.limits ?? me?.limits)?.["negf.enabled"];
  const toProps = useMutation({
    mutationFn: () => continueInProperties(job.id),
    onSuccess: (p) => { qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["me"] }); nav(`/projects/${p.id}`); },
    onError: (e) => notifyError(e),
  });
  const props = !!(project.limits ?? me?.limits)?.["properties.enabled"];
  const fig = useMemo(() => (r ? fitFigure(r) : null), [r]);
  const pmq = useParamMap(dftJobId ?? "", job.id);
  const pm = pmq.data && !isLimited(pmq.data) ? (pmq.data as ParamMap) : undefined;
  const [pick, setPick] = useState<LatticePick | null>(null);
  const [row, setRow] = useState<string | null>(null);
  const [rowBonds, setRowBonds] = useState<Set<number>>(new Set());
  useEffect(() => { setPick(null); setRow(null); setRowBonds(new Set()); }, [job.id]);
  const marked = useMemo(() => (row ? new Set([row]) : pickKeys(pm, pick)), [pm, pick, row]);
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());
  useEffect(() => {
    const first = r?.rows.find((p) => marked.has(p.key));
    if (first) rowRefs.current.get(first.key)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [marked, r]);
  const choose = (p: LatticePick | null) => { setPick(p); setRow(null); setRowBonds(new Set()); };
  const chooseRow = (key: string) => {
    if (row === key) { setRow(null); setRowBonds(new Set()); setPick(null); return; }
    const { pick: p, bonds } = rowPick(pm, key);
    setRow(key); setPick(p); setRowBonds(bonds);
  };
  if (!r || !fig) return <Text c="dimmed" mt="md">No fit result yet.</Text>;
  const atBound = r.rows.filter((p) => p.at_bound).length;
  const files = r.files ?? {};
  const copy = () => navigator.clipboard.writeText(paramsTsv(r)).then(() => notifyOk("Table copied."), notifyError);
  return (
    <Stack mt="md">
      {r.stop_reason && <Alert color="yellow" p="xs"><Text size="sm">{r.stop_reason}</Text></Alert>}
      {r.loaded_from && <Alert color="blue" p="xs"><Text size="sm">Parameters loaded from {r.loaded_from} (no refit).</Text></Alert>}
      <Grid gutter="md" columns={24}>
        <Grid.Col span={{ base: 24, lg: 9 }}>
          <Plot data={fig.data} layout={fig.layout} height={460} />
          <Text size="sm" c="dimmed">{fitMetrics(r)}</Text>
          {files.zip && (
            <Group gap="xs" mt="xs">
              <Button size="xs" variant="light" onClick={() => download(`/files/${files.zip}/download`, `${r.seed}_fit_results.zip`).catch(notifyError)}>
                Download fit files (.zip)
              </Button>
              <Text size="xs" c="dimmed">{Object.keys(files).filter((k) => k !== "zip").join(", ")} - also in this project's files</Text>
            </Group>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 24, lg: 7 }}>
          <Text fw={600} mb={4}>Model on the lattice</Text>
          {dftJobId ? <ParamLattice jobId={dftJobId} fitJobId={job.id} selected={pick} onSelect={choose} highlight={rowBonds} />
            : <Text size="sm" c="dimmed">Read the DFT data to see the model on the lattice.</Text>}
        </Grid.Col>
        <Grid.Col span={{ base: 24, lg: 8 }}>
          <Group justify="space-between" mb={4}>
            <Text fw={600}>Fitted parameters</Text>
            <Button size="compact-xs" variant="default" onClick={copy}>Copy table</Button>
          </Group>
          <Table.ScrollContainer minWidth={420} mah={440}>
            <Table stickyHeader striped verticalSpacing={2}>
              <Table.Thead>
                <Table.Tr><Table.Th>Group</Table.Th><Table.Th>Parameter</Table.Th><Table.Th>Value</Table.Th><Table.Th>Start</Table.Th><Table.Th>Bounds</Table.Th></Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {r.rows.map((p) => {
                  const c = p.at_bound ? "#b26a00" : undefined;
                  const on = marked.has(p.key);
                  const cells = [p.group, p.key, p.value.toFixed(5), p.initial == null ? "" : String(Number(p.initial.toPrecision(4))),
                    `[${Number(p.lo.toPrecision(4))}, ${Number(p.hi.toPrecision(4))}]`];
                  const row = (
                    <Table.Tr key={p.key} ref={(el) => { if (el) rowRefs.current.set(p.key, el); else rowRefs.current.delete(p.key); }}
                      onClick={() => chooseRow(p.key)} style={{ cursor: pm ? "pointer" : undefined }} data-marked={on || undefined}
                      bg={on ? "var(--mantine-color-yellow-light)" : undefined}>
                      {cells.map((t, i) => <Table.Td key={i}><Text size="xs" c={c} ff={i === 1 ? "monospace" : undefined}>{t}</Text></Table.Td>)}
                    </Table.Tr>
                  );
                  return p.at_bound
                    ? <Tooltip key={p.key} label="At a bound - widen it on the set-up screen if this is not intended.">{row}</Tooltip> : row;
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Text size="xs" c="dimmed" mt={4}>{r.rows.length} free parameters{atBound ? `; ${atBound} sit at a bound (orange).` : "."}
            {pm ? " Click a row to show where it acts on the lattice." : ""}</Text>
        </Grid.Col>
      </Grid>
      <PreviousRuns project={project} kind={job.kind === "skfitter.load" ? "skfitter.load" : "skfitter.fit"} current={job.id} onOpen={onOpen}
        describe={(p) => `${p.seed ?? ""} · ${Object.entries((p.cfg?.orbital_config ?? {}) as Record<string, string[]>).map(([e, o]) => `${e}: ${o.join(" ")}`).join(", ")}`
          + (p.opts ? ` · ${p.opts.method === "de" ? "DE" : `LS × ${p.opts.n_starts}`}` : "")} />
      <Group justify="space-between">
        <Button variant="default" onClick={onBack}>← Back to set-up</Button>
        <Group gap="xs">
          {negf && (
            <Tooltip label="A new NEGF Transport project with this fit as its model: devices, transmission, gates, magnetism">
              <Button variant="light" onClick={() => toNegf.mutate()} loading={toNegf.isPending} data-testid="use-in-negf">
                Use in NEGF Transport →
              </Button>
            </Tooltip>
          )}
          {props && (
            <Tooltip label="A new Properties project with this fit as its model: bands with weights, DOS, masses, Berry curvature, Chern numbers, AHC, SHC">
              <Button variant="light" onClick={() => toProps.mutate()} loading={toProps.isPending} data-testid="use-in-properties">
                Open in Properties →
              </Button>
            </Tooltip>
          )}
          <Button onClick={onNext}>Next: export &amp; properties →</Button>
        </Group>
      </Group>
    </Stack>
  );
}
